import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import AppSidebar from './shell/AppSidebar';
import MobileTabBar from './shell/MobileTabBar';
import ReferralReminder from './ReferralReminder';
import SiteAnnouncements from './SiteAnnouncements';
import ChatBubble from './ChatBubble';
import ChatWidget from './ChatWidget';
import UserManual from './UserManual';
import { subscribeToConversations, Conversation } from '../firebase/messaging';
import { playMessageSound, isNotificationSoundEnabled } from '../utils/notificationSound';
import { logPageView } from '../firebase/activityLog';
import { getUserProfileByUid, updateUserProfile } from '../firebase/firestore';
import TermsConsentModal from './TermsConsentModal';
import { TERMS_VERSION } from '../constants/terms';
import { useTranslation } from 'react-i18next';

interface LayoutProps {
  children: ReactNode;
  user?: any;
  onAuthSuccess?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onAuthSuccess }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const lastLoggedRef = useRef<{ path: string; timestamp: number } | null>(null);
  const [needsTermsConsent, setNeedsTermsConsent] = useState(false);
  const [termsProfileId, setTermsProfileId] = useState<string | null>(null);
  const [chatBubble, setChatBubble] = useState<{
    senderName: string;
    message: string;
    senderImageUrl?: string;
    conversationId: string;
  } | null>(null);
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [chatWidgetConversationId, setChatWidgetConversationId] = useState<string | undefined>();
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('kw-sidebar-collapsed') === '1'
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((collapsed) => {
      localStorage.setItem('kw-sidebar-collapsed', collapsed ? '0' : '1');
      return !collapsed;
    });
  };

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    const now = Date.now();
    const lastLogged = lastLoggedRef.current;

    if (lastLogged?.path === path && now - lastLogged.timestamp < 30000) {
      return;
    }

    lastLoggedRef.current = { path, timestamp: now };
    logPageView(path, user?.uid, user?.displayName || user?.email, user?.email)
      .catch((error) => console.error('Error logging page view:', error));
  }, [location.pathname, location.search, user]);

  useEffect(() => {
    const checkTerms = async () => {
      if (!user) {
        setNeedsTermsConsent(false);
        setTermsProfileId(null);
        return;
      }

      try {
        const profile = await getUserProfileByUid(user.uid);
        if (!profile) return;

        const hasAccepted = Boolean(profile.tosAcceptedAt);
        const versionMatches = profile.tosVersion === TERMS_VERSION;

        if (!hasAccepted || !versionMatches) {
          setNeedsTermsConsent(true);
          setTermsProfileId(profile.id);
        } else {
          setNeedsTermsConsent(false);
          setTermsProfileId(null);
        }
      } catch (error) {
        console.error('Error checking terms consent:', error);
      }
    };

    checkTerms();
  }, [user]);

  // Subscribe to conversations for chat bubble notifications
  useEffect(() => {
    if (!user) return;

    let previousConversations: Conversation[] = [];
    let isInitialLoad = true;

    const unsubscribeConversations = subscribeToConversations(user.uid, (conversations) => {
      // Calculate total unread messages
      const totalUnread = conversations.reduce((acc, conv) => acc + (conv.unreadCount?.[user.uid] || 0), 0);
      setTotalUnreadMessages(totalUnread);

      // Skip the initial load
      if (isInitialLoad) {
        previousConversations = conversations;
        isInitialLoad = false;
        return;
      }

      // Check for new unread messages
      conversations.forEach((conv) => {
        const unreadCount = conv.unreadCount?.[user.uid] || 0;
        const prevConv = previousConversations.find(c => c.id === conv.id);
        const prevUnreadCount = prevConv?.unreadCount?.[user.uid] || 0;

        // Check if there's a new message (not from the current user)
        if (unreadCount > prevUnreadCount && conv.lastMessage && conv.lastMessageSender !== user.uid) {
          // New message received - show chat bubble
          const senderName = conv.participantNames?.[conv.lastMessageSender || ''] || t('layout.someone');
          const senderImageUrl = conv.participantImages?.[conv.lastMessageSender || ''];

          // Play sound if enabled
          if (isNotificationSoundEnabled()) {
            playMessageSound();
          }
          
          // Show chat bubble
          setChatBubble({
            senderName,
            message: conv.lastMessage.substring(0, 100) + (conv.lastMessage.length > 100 ? '...' : ''),
            senderImageUrl,
            conversationId: conv.id
          });
        }
      });
      
      previousConversations = conversations;
    });

    return () => unsubscribeConversations();
  }, [user]);

  return (
    <div className="flex h-screen overflow-hidden bg-dark-bg">
      <SiteAnnouncements />
      <TermsConsentModal
        isOpen={needsTermsConsent}
        onAccept={async () => {
          if (!termsProfileId) return;
          await updateUserProfile(termsProfileId, {
            tosAcceptedAt: new Date(),
            tosVersion: TERMS_VERSION,
            locationConsent: true
          });
          setNeedsTermsConsent(false);
          setTermsProfileId(null);
        }}
      />
      <AppSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Shop Banner */}
        <div className="hidden">
          <p className="text-white font-semibold text-sm md:text-base">
            🎉 {t('layout.shopBanner')} 🎉
          </p>
        </div>
        <Header
          user={user}
          onAuthSuccess={onAuthSuccess}
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <main className="flex-1 overflow-y-auto bg-dark-bg pb-24 lg:pb-0">
          <ReferralReminder />
          {children}
        </main>
      </div>
      <MobileTabBar />
      
      {/* Chat Bubble */}
      {chatBubble && (
        <ChatBubble
          senderName={chatBubble.senderName}
          message={chatBubble.message}
          senderImageUrl={chatBubble.senderImageUrl}
          conversationId={chatBubble.conversationId}
          onClose={() => setChatBubble(null)}
          onOpenChatWidget={(conversationId) => {
            setChatWidgetConversationId(conversationId);
            setShowChatWidget(true);
          }}
        />
      )}
      
      {/* Floating Chat Button */}
      {user && !showChatWidget && (
        <button
          onClick={() => {
            setChatWidgetConversationId(chatBubble?.conversationId);
            setShowChatWidget(true);
            setChatBubble(null);
          }}
          className="fixed bottom-40 lg:bottom-20 right-4 z-40 rounded-full bg-accent-blue p-4 shadow-lg shadow-blue-900/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-accent-blue-strong"
          title={t('layout.openMessages')}
        >
          <MessageCircle className="w-6 h-6 text-white" />
          {totalUnreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {totalUnreadMessages > 9 ? '9+' : totalUnreadMessages}
            </span>
          )}
        </button>
      )}

      {/* Chat Widget */}
      <ChatWidget 
        isOpen={showChatWidget} 
        onClose={() => {
          setShowChatWidget(false);
          setChatWidgetConversationId(undefined);
        }}
        initialConversationId={chatWidgetConversationId}
      />
      
      {/* User Manual */}
      <UserManual />
    </div>
  );
};

export default Layout;
